import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { useAuthStore } from '../store/authStore';
import { ApiTokensSection } from '../components/ApiTokensSection';
import { ExportModal } from '../components/ExportModal';

export function SettingsPage() {
  const navigate = useNavigate();
  const clerk = useClerk();
  const { user } = useAuthStore();
  const [userIdCopied, setUserIdCopied] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const handleCopyUserId = async () => {
    if (!user?.id) return;
    try {
      await navigator.clipboard.writeText(user.id);
      setUserIdCopied(true);
      setTimeout(() => setUserIdCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy user ID:', err);
    }
  };

  const handleSignOut = async () => {
    await clerk.signOut();
  };

  return (
    <div className="min-h-screen bg-kanban-bg">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="btn-icon"
              title="Back to trips"
            >
              <span className="material-symbols-outlined text-xl">arrow_back</span>
            </button>
            <h1 className="text-lg font-bold text-text-primary tracking-tight">Settings</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Account Section */}
          <section className="bg-white rounded-2xl p-6 border border-border">
            <div className="flex items-start gap-3 mb-4">
              <span className="material-symbols-outlined text-2xl text-accent">
                account_circle
              </span>
              <div className="flex-1">
                <h2 className="text-base font-bold text-text-primary mb-1">Account</h2>
                <p className="text-sm text-text-secondary">
                  Manage your account and authentication.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* User Info */}
              {user && (
                <div className="space-y-3">
                  {user.email && (
                    <div>
                      <span className="text-xs font-medium text-text-tertiary uppercase tracking-wide">
                        Email
                      </span>
                      <p className="text-sm text-text-primary mt-0.5">{user.email}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-xs font-medium text-text-tertiary uppercase tracking-wide">
                      User ID
                    </span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <code className="text-xs text-text-secondary bg-surface-secondary px-2 py-1 rounded font-mono">
                        {user.id}
                      </code>
                      <button
                        onClick={handleCopyUserId}
                        className="btn-icon text-text-tertiary hover:text-text-primary"
                        title="Copy User ID"
                      >
                        <span className="material-symbols-outlined text-base">
                          {userIdCopied ? 'check' : 'content_copy'}
                        </span>
                      </button>
                      {userIdCopied && (
                        <span className="text-xs text-green-600">Copied!</span>
                      )}
                    </div>
                    <p className="text-xs text-text-tertiary mt-1">
                      Used for PDF import and API integrations.
                    </p>
                  </div>
                </div>
              )}

              {/* Sign Out */}
              <button
                onClick={handleSignOut}
                className="btn-secondary flex items-center gap-2 text-red-600 hover:bg-red-50"
              >
                <span className="material-symbols-outlined text-lg">logout</span>
                Sign Out
              </button>
            </div>
          </section>

          {/* API Tokens Section */}
          <ApiTokensSection />

          {/* Data Export Section */}
          <section className="bg-white rounded-2xl p-6 border border-border">
            <div className="flex items-start gap-3 mb-4">
              <span className="material-symbols-outlined text-2xl text-accent">download</span>
              <div className="flex-1">
                <h2 className="text-base font-bold text-text-primary mb-1">Data Export</h2>
                <p className="text-sm text-text-secondary">
                  Download your data as JSON for backup or to use with other tools.
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowExportModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-lg">download</span>
              Export Data
            </button>
          </section>
        </div>
      </div>

      {/* Export Modal */}
      <ExportModal open={showExportModal} onClose={() => setShowExportModal(false)} />
    </div>
  );
}
